/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */


import DbBaseObject from '@shared/DbBaseObject.js';
import PersonItem from "@/core/js/model/Invoice/PersonItem";

import BillingData from "@/core/js/model/Billing/BillingData";
import {plainToClass, Transform} from "class-transformer";
import {PaymentStatus} from "@/core/js/model/Billing/BillingItem";

import {copyObject} from "@/utils/helpers";

import {TypeClass} from "@qwoach/class-transformer-wrapper";
import Discriminator from "@/utils/Discriminator";
import InvoiceItemOld from "@shared/Invoice/InvoiceItemOld";


let invoicesGroupTransform = (obj) => {
	// console.log("invoicesGroupTransform: obj = ", obj)
	let billTo = obj.value || obj
	Object.keys(billTo.value || billTo).forEach(key => {
		// console.log("invoicesGroupTransform: key = ", key, ", billTo[key] = ", billTo[key])
		billTo[key] = plainToClass(InvoiceItemOld, billTo[key])
	})
	return billTo;
}

@TypeClass(Discriminator.create([
	{ value: () => InvoiceItemOld, name: "InvoiceItem" },
	{ value: () => InvoiceGroup, name: "InvoiceGroup"},
]))

class InvoiceGroup extends DbBaseObject{

	type: string = "InvoiceGroup"

	@Transform(invoicesGroupTransform)
	invoicesGroup = {} /** {personId1: InvoiceItemOld, personId2: InvoiceItemOld ...}  */

	userIdIndex = {}
	programIdIndex = {}


	constructor() {
		super();

	}

	createInvoiceFor(personItem: PersonItem, billingData: BillingData, selectedBillingItemIds: string[] = [], currencyItem){
		console.log("createInvoiceFor: currencyItem = ", currencyItem)
		let invoice = InvoiceItemOld.instance(billingData, selectedBillingItemIds, currencyItem)
		invoice.id = this.id
		invoice.billTo = personItem
		this.invoicesGroup[personItem.id] = invoice
		this.userIdIndex[personItem.id] = true
		invoice.invalidate(this)
	}

	setCurrencyItem(currencyItem){
		console.log("setCurrencyItem: currencyItem = ", currencyItem)
		Object.keys(this.invoicesGroup).forEach(personId => {
			let invoice = this.invoicesGroup[personId]
			invoice.currencyItem = currencyItem
		})
	}

	createInvoiceForAll(personItemArray: PersonItem[], billingData: BillingData, selectedBillingItemIds: string[] = [], currencyItem){
		personItemArray.forEach(personItem => this.createInvoiceFor(personItem, billingData, selectedBillingItemIds, currencyItem))
		this.setProgramId(billingData.programId)

	}

	addInvoiceFor(personItem: PersonItem, invoice: InvoiceItemOld){
		let invoiceCopy = copyObject(InvoiceItemOld, invoice)
		invoiceCopy.billTo = personItem
		this.invoicesGroup[personItem.id] = invoiceCopy
		this.userIdIndex[personItem.id] = true
	}
	removeInvoiceFor(personItem: PersonItem){
		delete this.invoicesGroup[personItem.id]
		delete this.userIdIndex[personItem.id]
	}
	addInvoiceForAll(personItemArray: PersonItem[], invoice: InvoiceItemOld){
		personItemArray.forEach(personItem => this.addInvoiceFor(personItem, invoice))
	}

	getInvoiceFor(personId: string){
		return this.invoicesGroup[personId]
	}

	forEachInvoice(callback){
		Object.values(this.invoicesGroup).forEach(invoice => callback(invoice))
	}
	findInvoice(callback){
		return Object.values(this.invoicesGroup).find(invoice => callback(invoice))
	}
	reduceInvoice(callback, defaultValue){
		return Object.values(this.invoicesGroup).reduce((accumulated, invoice) => callback(accumulated, invoice), defaultValue)
	}

	findInvoiceByBillingItemId(billingItemId){
		console.log("findInvoiceByBillingItemId this = ", this)
		return this.findInvoice(invoice => {
			console.log("findInvoiceByBillingItemId: invoice = ", invoice)
			return invoice.billingData.findBillingItemById(billingItemId) != null
		})
	}


	setProgramId(programId: string){
		this.forEachInvoice(invoice => {
			invoice.programId = programId;
		})
		if(programId){
			this.programIdIndex[programId] = true
		}
	}

	setTitle(title: string){
		this.forEachInvoice(invoice => invoice.title = title)
	}

	setDetailsUrl(detailsUrl: string){
		this.forEachInvoice(invoice => invoice.detailsUrl = detailsUrl)
	}
	setDetailsUrlEach(callback){
		this.forEachInvoice(invoice => invoice.detailsUrl = callback(invoice))
	}

	setProgramDetailsUrl(programDetailsUrl: string){
		this.forEachInvoice(invoice => invoice.programDetailsUrl = programDetailsUrl)
	}

	setBillFrom(billFrom: PersonItem){
		this.forEachInvoice(invoice => invoice.billFrom = billFrom)
	}

	setPaymentInstructions(paymentInstructions){
		this.forEachInvoice(invoice => invoice.paymentInstructions = paymentInstructions)
	}



	isEmailSentAll(){
		let res = this.reduceInvoice((res, invoice) => res && invoice.isEmailSent, true)
		return res
	}

	generalInvoice(){
		//TODO return the first invoice for now. Currently all the invoices are the same but this will change in future
		return Object.values(this.invoicesGroup)[0]
	}

	paidDateTsIfAllSame(){
		let res = 0;
		if(this.userIdIndex.length > 0){
			let firstUserId = this.userIdIndex[0]
			let firstInvoice = this.invoicesGroup[firstUserId]
			if(firstInvoice){
				res = this.reduceInvoice((res, invoice) => res == invoice.paidDateTs ? res : 0, firstInvoice.paidDateTs)
			}
		}
		return res
	}

	setIsEmailSent(isEmailSent){
		this.forEachInvoice(invoice => invoice.isEmailSent = isEmailSent)
	}


	invalidate(){
		this.forEachInvoice(invoice => invoice.invalidate(this))
	}


	setPaymentStatus(paymentStatus, actionDateTs){
		let functionName = ""
		switch (paymentStatus) {
			case PaymentStatus.PAID:
				functionName = "setPaid"
				break;
			case PaymentStatus.INVOICED:
				functionName = "setInvoiced"
				break;
			case PaymentStatus.CONFIRM_PENDING:
				functionName = "setConfirmPending"
				break;
			default:
			case PaymentStatus.NONE:
				functionName = "setNone"
				break;
		}
		this.forEachInvoice(invoice => {
			invoice[functionName](actionDateTs)
			invoice.invalidate(this)
		})

	}


	isPaymentStatusNone(){
		return this.findInvoice(invoice => invoice.paymentStatus === PaymentStatus.NONE)
	}
	isPaymentStatusDeleted(){
		return this.findInvoice(invoice => invoice.paymentStatus === PaymentStatus.DELETED)
	}
	isPaid(){
		return this.findInvoice(invoice => invoice.paymentStatus === PaymentStatus.PAID)
	}
	isPartiallyPaid(){
		return this.paymentStatus === PaymentStatus.PARTIALLY_PAID
	}

	isInvoiced(){
		return this.findInvoice(invoice => invoice.paymentStatus === PaymentStatus.INVOICED)
	}
	isConfirmPending(){
		return this.findInvoice(invoice => invoice.paymentStatus === PaymentStatus.CONFIRM_PENDING)
	}


}



export default InvoiceGroup
